import {
  ColorSchemeType,
  FlagLabel,
  getColorsFromScheme,
  getFlagElementSizes,
} from 'platform/components';
import {
  Box,
  Center,
  HStack,
  Icon,
  Integer,
  Show,
  ThemeColorPath,
  ThemeIconKey,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {suffixTestId, TestIdProps} from 'shared/web';

interface HighlightFlagProps extends TestIdProps {
  label: string;
  colorScheme?: ColorSchemeType;
  isSubtle?: boolean;
  size?: 'small' | 'default';
  /** Icon in front of the label, in the label color. */
  icon?: ThemeIconKey;
  /** 1px outline, for a background that matches the chip (e.g. a selected table row). */
  isOutlined?: boolean;
}

/**
 * Sourcing highlight chip: looks like the platform `Flag`, plus an optional leading icon and an
 * optional 1px outline in a darker tone of its color, so it stays visible on a selected (blue.20)
 * table row whose background matches the chip.
 */
export function HighlightFlag(props: HighlightFlagProps) {
  const {height, labelHorizontalPadding, textSize, borderRadius} = getFlagElementSizes(props.size);
  const {backgroundColor, color} = getColorsFromScheme(props.colorScheme, props.isSubtle);
  const iconSize = match<HighlightFlagProps['size'], Integer>(props.size)
    .with('small', () => 3)
    .otherwise(() => 4);

  return (
    <HStack justify="flex-start" maxWidth="100%">
      <Box
        position={props.isOutlined ? 'relative' : undefined}
        height={height}
        paddingHorizontal={labelHorizontalPadding}
        backgroundColor={backgroundColor}
        borderRadius={borderRadius}
        maxWidth="100%"
      >
        <Center height="100%">
          <HStack align="center" spacing={1} minWidth={0}>
            <Show when={props.icon}>
              {(icon) => (
                <Icon
                  value={icon}
                  size={iconSize}
                  color={color}
                  data-testid={suffixTestId('highlightFlagIcon', props)}
                />
              )}
            </Show>
            <Box minWidth={0}>
              <FlagLabel
                color={color}
                size={textSize}
                label={props.label}
                data-testid={props['data-testid']}
              />
            </Box>
          </HStack>
        </Center>
        {/* The outline is an overlay, so the chip keeps exactly the size of a platform Flag */}
        <Show when={props.isOutlined}>
          <Box
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            left={0}
            border="1px solid"
            borderColor={getOutlineColor(props.colorScheme, props.isSubtle)}
            borderRadius={borderRadius}
            pointerEvents="none"
            data-testid={suffixTestId('highlightFlagOutline', props)}
          />
        </Show>
      </Box>
    </HStack>
  );
}

/** One tone darker than the subtle chip background (palette .40, neutral .50). */
const SUBTLE_OUTLINE_COLORS: Record<ColorSchemeType, ThemeColorPath> = {
  neutral: 'palettes.neutral.50.100',
  black: 'palettes.neutral.50.100',
  red: 'palettes.red.40.100',
  magenta: 'palettes.magenta.40.100',
  purple: 'palettes.purple.40.100',
  blue: 'palettes.blue.40.100',
  teal: 'palettes.teal.40.100',
  green: 'palettes.green.40.100',
  yellow: 'palettes.yellow.40.100',
  orange: 'palettes.orange.40.100',
};

/** Outline tone of a chip: a step darker than its background (the hover tone for solid chips). */
const getOutlineColor = (colorScheme: ColorSchemeType = 'neutral', isSubtle = false) =>
  isSubtle ? SUBTLE_OUTLINE_COLORS[colorScheme] : getColorsFromScheme(colorScheme).hoverColor;
